import { type MonitorAnalysis, type MonitorImpactType } from '@/types/bill'
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { ArrowUpCircle, ArrowDownCircle, CircleDot } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface BillImpactCellProps {
  analysis: MonitorAnalysis | undefined
  onClick?: () => void
}

interface StyleConfig {
  bg: string
  text: string
  icon: typeof ArrowUpCircle | typeof ArrowDownCircle | typeof CircleDot
}

const impactStyles: Record<MonitorImpactType, StyleConfig> = {
  positive: {
    bg: "bg-green-100 hover:bg-green-200",
    text: "text-green-800",
    icon: ArrowUpCircle
  },
  negative: {
    bg: "bg-red-100 hover:bg-red-200",
    text: "text-red-800",
    icon: ArrowDownCircle
  },
  neutral: {
    bg: "bg-gray-100 hover:bg-gray-200",
    text: "text-gray-800",
    icon: CircleDot
  }
}

export function BillImpactCell({ analysis, onClick }: BillImpactCellProps) {
  if (!analysis?.summary?.overall_impact) {
    return (
      <Badge variant="secondary" className="bg-gray-100 text-gray-800">
        <CircleDot className="h-4 w-4 mr-1" />
        No Analysis
      </Badge>
    )
  }

  const { type, score } = analysis.summary.overall_impact
  const style = impactStyles[type]
  const Icon = style.icon

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="secondary" 
            className={cn(
              "cursor-pointer transition-colors",
              style.bg,
              style.text
            )}
            onClick={onClick}
          >
            <Icon className="h-4 w-4 mr-1" />
            Impact: {score}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>Click to view full analysis</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}